/**
 * Axis-label policy shared by every cartesian chart view (and therefore by the
 * dashboard chart tile, the Query Editor chart panel, Table Explorer charts and
 * notebook chart cells).
 *
 * Two concerns, both driven by the fact that a chart is often rendered into a
 * small tile:
 *
 * 1. `abbreviateAxisTick` — axis TICKS get a compact form (16k, 1.2M) so the
 *    Y axis doesn't spend half a narrow tile's width on digits. Tooltips and
 *    data labels keep the full, locale-grouped value (`formatTooltipValue`) —
 *    abbreviation is a reading aid on the axis, never the number of record.
 * 2. `xAxisTickLayout` — X tick labels rotate ONLY when horizontal labels would
 *    collide. Single-character categories ("A", "B") stay horizontal, and the
 *    axis then reserves ~28px instead of the ~120px a fixed 45° rotation used
 *    to cost — that reserved-but-empty band is what left tall tiles rendering
 *    the plot in a third of their height.
 */

/** Descending so the first match is the largest applicable unit. */
const UNITS = [
  { limit: 1e12, suffix: 'T' },
  { limit: 1e9, suffix: 'B' },
  { limit: 1e6, suffix: 'M' },
  { limit: 1e3, suffix: 'k' },
] as const;

/**
 * Compact axis-tick label: `16000` → `16k`, `1200000` → `1.2M`. Values below
 * 1000 stay exact (rounded to at most 2 decimals so a float tick like
 * `0.30000000000000004` doesn't render its binary tail). Non-numeric ticks
 * (category axes) pass through unchanged.
 */
export function abbreviateAxisTick(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value === null || value === undefined ? '' : String(value);
  }
  const abs = Math.abs(value);
  if (abs < 1000) return String(Number(value.toFixed(2)));
  for (const { limit, suffix } of UNITS) {
    if (abs >= limit) {
      const scaled = value / limit;
      // One decimal while the mantissa is single-digit (1.2M), none above (16k).
      return `${Number(scaled.toFixed(Math.abs(scaled) < 10 ? 1 : 0))}${suffix}`;
    }
  }
  /* c8 ignore next */
  return String(value);
}

/** Full, locale-grouped value for tooltips — the number of record. */
export function formatTooltipValue(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString()
    : String(value ?? '');
}

export type XAxisTickLayout = {
  /** Recharts `angle` for the tick text (0 = horizontal). */
  angle: number;
  textAnchor: 'end' | 'middle';
  /** Vertical space the axis reserves for its labels, in px. */
  height: number;
};

const TICK_FONT_SIZE = 12;
/** Mean glyph advance of the 12px UI font — good enough to predict collisions. */
const CHAR_WIDTH = TICK_FONT_SIZE * 0.55;
/** Minimum breathing room between two horizontal tick labels. */
const LABEL_GAP = 10;
/** Tick marks + the gap between the axis line and the label text. */
const AXIS_PADDING = 14;
const MIN_AXIS_HEIGHT = 28;
const MAX_AXIS_HEIGHT = 72;
/**
 * Container width the plot area does NOT get: left/right chart margins plus the
 * Y axis gutter. Subtracted from the measured container width before deciding.
 */
const NON_PLOT_WIDTH = 90;
/** Used before the container has been measured (first paint). */
const FALLBACK_PLOT_WIDTH = 360;

const HORIZONTAL: XAxisTickLayout = { angle: 0, textAnchor: 'middle', height: MIN_AXIS_HEIGHT };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Decides whether the X tick labels must rotate, and how much vertical space
 * the axis needs either way.
 *
 * @param labels     the category values rendered on the axis
 * @param containerWidth measured chart container width in px; omit (or 0)
 *                       before the first measurement and a mid-size tile is
 *                       assumed
 */
export function xAxisTickLayout(labels: readonly unknown[], containerWidth?: number): XAxisTickLayout {
  if (labels.length === 0) return HORIZONTAL;
  const widest = labels.reduce<number>(
    (max, label) => Math.max(max, String(label ?? '').length * CHAR_WIDTH),
    0,
  );
  if (widest === 0) return HORIZONTAL;

  const plotWidth =
    containerWidth && containerWidth > 0
      ? Math.max(containerWidth - NON_PLOT_WIDTH, 1)
      : FALLBACK_PLOT_WIDTH;
  if (labels.length * (widest + LABEL_GAP) <= plotWidth) return HORIZONTAL;

  // Rotated 45°: the label's vertical footprint is its width × sin(45°).
  return {
    angle: -45,
    textAnchor: 'end',
    height: clamp(Math.round(widest * Math.SQRT1_2) + AXIS_PADDING, MIN_AXIS_HEIGHT, MAX_AXIS_HEIGHT),
  };
}

/**
 * Plot margins for the cartesian views. `bottom` is intentionally small: the
 * X axis reserves its own label space via `XAxisTickLayout.height`, so a large
 * bottom margin here would double-book (and waste) the tile's height.
 */
export const CARTESIAN_MARGIN = { top: 12, right: 24, left: 8, bottom: 4 } as const;

/** X tick values of a transformed dataset, for `xAxisTickLayout`. */
export function xAxisLabels(
  data: readonly Record<string, unknown>[],
  xDataKey: string,
): unknown[] {
  return data.map((point) => point[xDataKey]);
}
